import {t} from "@lingui/core/macro";
import _ from "lodash";

import {isDevice} from "./device";
import {formatAmount} from "./strings";

export const INFO = "info";
export const ERROR = "error";

type ValidationLevel = typeof INFO | typeof ERROR;

export type ValidationResult = {
    failed: boolean;
    message?: string;
    level?: ValidationLevel;
};

export const validate = (errorMessage: string | null = null): ValidationResult => {
    if (!_.isNil(errorMessage)) return {failed: true, message: errorMessage};
    return {failed: false};
};

const isInt = (value: string) => {
    const reg = /^\d+$/;
    return reg.test(value);
};

export const matches =
    (regexp: RegExp, errorMessage = "Ogiltig värde.") =>
    (value: string) => {
        let message;
        if (!regexp.test(value)) {
            message = errorMessage;
        }
        return validate(message);
    };

export const getEmailRegex = () => {
    const ATOM =
        "[a-z0-9_'\\+~\\/µÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÑÒÓÔÕÖÙÚÛÜÝßàáâãäåæçèéêëìíîïñòóôõöùúûüýÿ-]";
    const TLD = "[a-z]";
    const DOMAIN = `(${ATOM}+(\\.${ATOM}+)*(\\.${TLD}{2,})+`;
    const regex = new RegExp(`^${ATOM}+(\\.${ATOM}+)*@${DOMAIN})$`, "ig");

    return regex;
};

export const email = (value: string): ValidationResult => {
    let message;

    // rules for email validation: https://jira-atg.riada.cloud/browse/ATGSEDIG-37917
    const regex = getEmailRegex();
    const emailLength = value.substr(0, value.indexOf("@")).length;

    if (!regex.test(value)) {
        message = "Ogiltig e-postadress.";
    }
    if (emailLength > 64) {
        message = "Ogiltig e-postadress. Max 64 tecken före @";
    }

    return validate(message);
};

export const username = (value: string): ValidationResult => {
    let message;
    if (value === "") return validate("Skriv in användarnamn.");

    if (!/^[a-z\d]*$/gi.test(value)) {
        message =
            "Användarnamnet innehåller ogiltiga tecken. Giltiga tecken är bokstäver (a-z) och siffror.";
    } else if (!/^[a-z]{6}\d{3}$/gi.test(value)) {
        message =
            "Användarnamnet innehåller inte rätt antal tecken. Ditt användarnamn består av sex bokstäver följt av tre siffror.";
    }

    return validate(message);
};

export const password = (value: string): ValidationResult => {
    let message;
    const length = _.size(value);
    if (value === "") return validate(t`Skriv in lösenord.`);

    if (!/^[a-z\d]*$/gi.test(value)) {
        message = t`Lösenordet innehåller ogiltiga tecken. Giltiga tecken är bokstäver och siffror.`;
    } else if (length < 8 || length > 12) {
        message = t`Lösenordet innehåller inte rätt antal tecken. Ditt lösenord består av 8-12 tecken.`;
    }

    return validate(message);
};

export const updatePassword = (value: string): ValidationResult => {
    const validator = password(value);

    if (!validator.failed) {
        const digitCount = (value.match(/\d/g) || []).length;
        const upperCount = (value.match(/[A-Z]/g) || []).length;
        const lowerCount = (value.match(/[a-z]/g) || []).length;

        if (
            digitCount < 1 ||
            digitCount > 6 ||
            upperCount < 1 ||
            lowerCount < 1 ||
            digitCount + upperCount + lowerCount !== value.length
        ) {
            validator.failed = true;
            validator.message = t`Lösenordet har ett ogiltigt format. Det måste innehålla minst en siffra och max sex siffror, minst en liten bokstav (a-z) och en stor bokstav (A-Z).`;
        }
    }

    return validator;
};

export const checkPersonNummer = (value: string): boolean => {
    let cleanPSN = value.replace("-", "");
    cleanPSN = cleanPSN.length > 10 ? cleanPSN.substr(2) : cleanPSN;

    const numberArray = cleanPSN.split("").map((x) => Number(x));

    const expectedValue = numberArray.pop();

    const totalSum = numberArray.reduce((prev, current, currentIndex) => {
        const factor = currentIndex === 0 || currentIndex % 2 === 0 ? 2 : 1;
        const result = current * factor;
        if (result > 9) {
            return prev + (result - 9);
        }
        return prev + result;
    }, 0);

    const totalSumString = totalSum.toString();

    let actualValue =
        10 -
        Number(totalSumString.length > 1 ? totalSumString.substr(1) : totalSumString);
    actualValue = actualValue === 10 ? 0 : actualValue;

    return expectedValue === actualValue;
};

export const personNummer = (value: string): ValidationResult => {
    let message;

    const format = /^\d{8}-\d{4}$/gi.test(value); // yyyymmdd-xxxx

    if (format && checkPersonNummer(value)) {
        return validate(message);
    }

    return validate("Ogiltigt personnummer.");
};

export const isPersonalIdentityNumberFormatValid = (value: string): boolean => {
    const format13Chars = /^\d{8}-\d{4}$/gi.test(value); // yyyymmdd-xxxx
    const format12Chars = /^\d{12}$/gi.test(value); // yyyymmddxxxx
    const format11Chars = /^\d{6}-\d{4}$/gi.test(value); // yymmdd-xxxx
    const format10Chars = /^\d{10}$/gi.test(value); // yymmddxxxx

    if (format10Chars || format11Chars || format12Chars || format13Chars) {
        return true;
    }
    return false;
};

export const isDateValid = (value: string): boolean => {
    if (value.length !== 6 && value.length !== 8) {
        return false;
    }

    let dateOfBirth = value;
    if (value.length === 6) {
        const decade = parseInt(`19${value.substring(0, 2)}`, 10);
        if (new Date().getFullYear() - decade > 100) {
            dateOfBirth = `20${dateOfBirth}`;
        } else {
            dateOfBirth = `19${dateOfBirth}`;
        }
    }

    const year = parseInt(dateOfBirth.substring(0, 4), 10);
    const month = parseInt(dateOfBirth.substring(4, 6), 10) - 1;
    const day = parseInt(dateOfBirth.substring(6, 8), 10);
    const date = new Date(year, month, day);
    // eslint-disable-next-line eqeqeq
    if (date.getFullYear() == year && date.getMonth() == month && date.getDate() == day) {
        return true;
    }
    return false;
};

export const validatePersonalIdentityNumber = (value: string): ValidationResult => {
    const invalidPersonalIdentityNumberResponse = {
        failed: true,
        message: "Ogiltigt personnummer.",
    };
    if (!isPersonalIdentityNumberFormatValid(value)) {
        return invalidPersonalIdentityNumberResponse;
    }
    if (value.length < 12) {
        if (!isDateValid(value.substring(0, 6))) {
            return invalidPersonalIdentityNumberResponse;
        }
    } else if (!isDateValid(value.substring(0, 8))) {
        return invalidPersonalIdentityNumberResponse;
    }
    return {
        failed: false,
    };
};

export const mobileNumber = (value: string): ValidationResult => {
    if (!value) return validate("Skriv in mobilnummer");

    const normalized = value.replace(/\s/g, "");

    const validPatterns = [
        /^07\d{8}$/,
        /^07\d{2}-\d{6}$/,
        /^07\d{1}-\d{7}$/,
        /^\+467\d{8}$/,
        /^00467\d{8}$/,
    ];

    const isValid = validPatterns.some((pattern) => pattern.test(normalized));

    return validate(isValid ? undefined : "Ogiltigt mobilnummer");
};

export const phoneNumber = (value: string): ValidationResult => {
    let message;
    if (value === "") return validate("Skriv in telefonnummer");
    if (!/^(07\d{8})?$/.test(value)) {
        message = "Ogiltigt telefonnummer, ange endast 10 siffror och inled med 07.";
    }

    return validate(message);
};

export const internationalPhoneNumber = (value: string): ValidationResult => {
    let message;

    if (value === "") return validate("Ange ditt telefonnummer, inklusive landskod");

    if (!/^(\+|00)[1-9]{1}[0-9]{7,14}$/.test(value)) {
        message =
            "Ogiltigt telefonnummer. Du behöver ange landskod, t.ex. +46xxxxxxx eller 0046xxxxxxxx";
    }

    return validate(message);
};

export const emailOrPhoneNumber = (value: string): ValidationResult => {
    let message;
    if (!phoneNumber(value).failed || !email(value).failed) {
        return validate(message);
    }
    return validate("Ej giltig e-post eller telefonnummer");
};

export const isNumber = (value: string): ValidationResult => {
    let message;

    if (_.isEmpty(value)) {
        message = "Du måste ange ett värde";
    } else if (!isInt(value)) {
        message = "Värdet som angetts är inte en siffra";
    }

    return validate(message);
};

export const notEmpty = (value: string): ValidationResult => {
    let message;

    if (!(value && value.length > 0)) {
        message = "Fältet får inte vara tomt.";
    }

    return validate(message);
};

export const minValue = (min: number, minValueMsg: string) => (value: number) => {
    let message;

    if (value < min) {
        message =
            minValueMsg || `Värdet som angetts är för lågt. Lägsta tillåtna är ${min}.`;
    }

    return validate(message);
};

export const maxValue = (max: number, minValueMsg: string) => (value: number) => {
    let message;

    if (value > max) {
        message =
            minValueMsg || `Värdet som angetts är för högt. Högsta tillåtna är ${max}.`;
    }

    return validate(message);
};

type IntervalValue = {
    value: number | null | undefined;
    min?: number;
    max?: number;
};

type ValueMessage = string | ((value: IntervalValue) => string);

const evalValueMessage = (message: ValueMessage | null, value: IntervalValue) => {
    if (typeof message === "string") {
        return message;
    }
    if (typeof message === "function") {
        return message(value);
    }
    return null;
};

export type ValidateIntervalOptions = {
    noValueMessage?: ValueMessage;
    nonNumberMessage?: ValueMessage;
    tooLowMessage?: ValueMessage;
    tooHighMessage?: ValueMessage;
    min?: number;
    max?: number;
};

export const validateInterval =
    ({
        noValueMessage = "Du måste ange ett värde",
        nonNumberMessage = "Värdet som angetts är inte en siffra",
        tooLowMessage = ({min}) => `Värdet som angetts är för lågt. Lägst ${min}.`,
        tooHighMessage = ({max}) => `Värdet som angetts är för stort. Högst ${max}.`,
        min,
        max,
    }: ValidateIntervalOptions = {}) =>
    (value?: number): ValidationResult => {
        let message = null;
        if (!_.isNumber(value)) {
            message = noValueMessage;
        } else if (!_.isNumber(value) || !Number.isSafeInteger(value)) {
            message = nonNumberMessage;
        } else if (_.isNumber(min) && value < min) {
            message = tooLowMessage;
        } else if (_.isNumber(max) && value > max) {
            message = tooHighMessage;
        }
        const errorMessage = evalValueMessage(message, {value, min, max});
        return validate(errorMessage);
    };

const formatMoney = (value?: number) =>
    value != null ? `${formatAmount(Math.ceil(value / 100))}` : "";

export const validateMoneyInterval = ({
    tooLowMessage = ({min}) =>
        `Värdet som angetts är för lågt. Lägst ${formatMoney(min)} kr.`,
    tooHighMessage = ({max}) =>
        `Värdet som angetts är för stort. Högst ${formatMoney(max)} kr.`,
    ...options
}: ValidateIntervalOptions = {}) =>
    validateInterval({
        tooLowMessage,
        tooHighMessage,
        ...options,
    });

const formatTime = (minutesInput: number) => {
    const hours = Math.floor(minutesInput / 60);
    const minutes = minutesInput % 60;

    if (!hours) return `${minutes} minuter`;
    if (!minutes) return `${hours} timmar`;
    return `${hours} timmar ${minutes} minuter`;
};

type TimeValidationOptions = {
    min?: number;
    max?: number;
    tooLowMessage?: string;
};

const timeRegEx = /^(?:[0-9]*(?::[0-9]{1,2})?|0[0-9]{3})$/;
const altTimeFormat = /^(0[0-9])([0-9]{2})$/;

export const validateTime =
    ({min = 0, max, tooLowMessage}: TimeValidationOptions) =>
    (value: string | undefined | null) => {
        if (!value) return validate("Du måste ange ett värde.");

        if (!timeRegEx.test(value))
            return validate("Värdet måste vara skrivet i formatet Timmar:Minuter.");

        let [stringHours, stringMinutes] = value.split(":");

        [, stringHours, stringMinutes] = altTimeFormat.exec(value) || [
            undefined,
            stringHours,
            stringMinutes,
        ];

        let minutes = stringMinutes ? parseInt(stringMinutes, 10) : 0;

        if (minutes > 59) return validate("Minuter måste vara under 60.");

        const hours = stringHours ? parseInt(stringHours, 10) : 0;
        minutes += hours * 60;

        if (minutes < min)
            return validate(
                tooLowMessage || `Värdet kan inte vara mindre än ${formatTime(min)}.`,
            );
        if (max && minutes > max)
            return validate(`Värdet kan inte överstiga ${formatTime(max)}.`);

        return validate();
    };

export const minInputLength = (minLength: number) => (value: string) => {
    let message;
    const length = value ? value.length : 0;

    if (length < minLength) {
        message = `Du måste ange minst ${minLength} tecken.`;
    }

    return validate(message);
};

export const lengthInterval =
    (minLength: number, maxLength: number) => (value: string) => {
        let message;
        const length = value ? value.length : 0;

        if (length < minLength) {
            message = `Du måste ange minst ${minLength} tecken.`;
        } else if (length > maxLength) {
            message = `Du måste ange max ${maxLength} tecken.`;
        }

        return validate(message);
    };

export const landLinePhoneNumber = (value: string): ValidationResult => {
    if (value && !isInt(value)) {
        return validate("Telefonnumret får bara innehålla siffror");
    }

    return lengthInterval(0, 14)(value);
};

export const capsLock = (keyEvent: KeyboardEvent): ValidationResult => {
    const isDesktop = !isDevice();
    let message;

    if (isDesktop) {
        const s = keyEvent.key;
        if (s.toUpperCase() === s && s.toLowerCase() !== s && !keyEvent.shiftKey) {
            message = "Caps Lock är aktiv!";
        }
    }

    return validate(message);
};

export const betCode = (value: string): ValidationResult => {
    let message;

    if (value.length !== 9) {
        message = "Rättningskoden består av 9 bokstäver";
    }

    return validate(message);
};

export const betCorrectionCode = (value: string): ValidationResult | string => {
    let message;

    if (value === "") return value;

    if (value.length !== 9) {
        message = "Rättningskoden består av 9 bokstäver";
    }

    return validate(message);
};

export const smsCode = (value: string): ValidationResult => {
    let message;

    let validator = isNumber(value);
    if (validator.failed) {
        message = validator.message;
    }

    validator = lengthInterval(6, 6)(value);
    if (validator.failed) {
        message = validator.message;
    }

    return validate(message);
};

export const MIN_DEPOSIT_MESSAGE = () => t`Minsta belopp är %1 kr.`;
export const MAX_DEPOSIT_MESSAGE = () => t`Maximalt belopp är %1 kr.`;
const MAX_BUDGET_MESSAGE = () =>
    t`Beloppet överskrider din insättningsgräns för dag, vecka eller månad. \nMax att sätta in idag är %1 kr.`;
const MIN_VALUE_MESSAGE = () => t`Värdet som angetts är för lågt. Lägst %1 kr.`;
const MAX_VALUE_MESSAGE = () => t`Värdet som angetts är för stort. Högst %1 kr.`;
const NO_VALUE_MESSAGE = () => t`Du måste ange ett värde`;
const NON_NUMBER_MESSAGE = () => t`Värdet som angetts är inte en siffra`;

/**
 * const user = "Alice";
 * return matchValueToMessage("Hello %1", user); // Hello Alice
 */
type StringOrNumber = string | number;
const matchValueToMessage = (translatedText: string, ...args: StringOrNumber[]) =>
    translatedText.replace(/%(\d+)/g, (match, number) =>
        typeof args[number - 1] !== "undefined" ? String(args[number - 1]) : match,
    );

export type ErrorObject = {
    failed: boolean;
    message?: string;
};

export type Validator = (value: number) => ErrorObject | false;

const getErrorObject = (errorMessage: string) => ({failed: true, message: errorMessage});
export const getNonErrorObject = () => ({failed: false});

export const validateValueToList = (
    value: number,
    list: Array<Validator>,
): Array<ErrorObject> =>
    _.reduce(
        list,
        (result, validator) => {
            const validationError = validator(value);
            if (validationError) result.push(validationError);
            return result;
        },
        [] as Array<ErrorObject>,
    );

// --- Validator creators ---

export const getMinValueValidator =
    (min: number, errorMessage: string = MIN_VALUE_MESSAGE()) =>
    (value: number) =>
        value < min &&
        getErrorObject(matchValueToMessage(errorMessage, formatMoney(min)));

export const getMaxValueValidator =
    (max: number, errorMessage: string = MAX_VALUE_MESSAGE()) =>
    (value: number) =>
        value > max &&
        getErrorObject(matchValueToMessage(errorMessage, formatMoney(max)));

export const getNoValueValidator =
    (errorMessage: string = NO_VALUE_MESSAGE()) =>
    (value: number | string | null | undefined) =>
        (_.isNaN(value) || !_.isNumber(value)) && getErrorObject(errorMessage);

export const getNonNumberValueValidator =
    (errorMessage: string = NON_NUMBER_MESSAGE()) =>
    (value: number | string | undefined) =>
        !Number.isSafeInteger(value) && getErrorObject(errorMessage);

// --- Validator Lists ---

export const getDepositValidatorList = (min: number, remainingDepositBudget: number) => [
    getNoValueValidator(),
    getNonNumberValueValidator(),
    getMinValueValidator(min, MIN_DEPOSIT_MESSAGE()),
    getMaxValueValidator(remainingDepositBudget, MAX_BUDGET_MESSAGE()),
];

export const getAmountValidatorList = (min: number, max: number) => [
    getNoValueValidator(),
    getNonNumberValueValidator(),
    getMinValueValidator(min),
    getMaxValueValidator(max),
];
